import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const ProdutoPDF = () => {
  const location = useLocation();
  const [data, setData] = useState(location.state.pdfData || []);
  const pdfRef = useRef();

  useEffect(() => {
    const generatePDF = async () => {
      const doc = new jsPDF("landscape", "mm", "a4");
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();
      
      // Dividir os dados em grupos de 6 produtos por página
      const produtosPorPagina = 6;
      const totalPaginas = Math.ceil(data.length / produtosPorPagina);
      const incluirImagens = true; // Ativar imagens no PDF
      
      // Limitar número de páginas para evitar PDF muito grande
      const maxPaginas = 2000; // Máximo 20 páginas (120 produtos)
      const paginasLimitadas = Math.min(totalPaginas, maxPaginas);
      
   
      
      if (totalPaginas > maxPaginas) {
        console.warn(`⚠️ Muitos produtos! Limitando a ${maxPaginas} páginas (${maxPaginas * produtosPorPagina} produtos)`);
      }
      
      for (let pagina = 0; pagina < paginasLimitadas; pagina++) {
        if (pagina > 0) {
          doc.addPage();
        }
        
        // Calcular quais produtos vão nesta página
        const inicio = pagina * produtosPorPagina;
        const fim = Math.min(inicio + produtosPorPagina, data.length);
        const produtosPagina = data.slice(inicio, fim);
        
        console.log(`Processando página ${pagina + 1}/${paginasLimitadas} - Produtos ${inicio + 1} a ${fim}`);
        
        // Cabeçalho da página
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("Relatório de Produtos - Oportunidade", 10, 15);
        
        // Informações da oportunidade
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text(`Número da Oportunidade: ${location.state.codigo?.toString()?.padStart(6, "0")}`, 10, 25);
        doc.text(`Data de Criação: ${dayjs(location.state.dataAbertura).format("DD/MM/YYYY")}`, 10, 30);
        doc.text(`Seller: ${location.state.seller?.label || ""}`, 10, 35);
        doc.text(`TP: TP ${location.state.tp || ""}`, 10, 40);
        
        // Cabeçalho da tabela
        let yPos = 50;
        // Larguras otimizadas: Total = ~277mm (cabe em A4 paisagem com margens)
        const colWidths = [10, 18, 45, 20, 15, 14, 18, 15, 15, 19, 18, 19, 18];
        const headers = ["#", "Img", "Produto", "GTN/EAN", "SKU", "Lote", "Data\nValid.", "Estoque\n(cx)", "Unid.\n/Cx", "Preço\n/Cx", "Preço\nUnit.", "Preço\nc/ ST", "Preço\nc/ FEE"];

        // Desenhar cabeçalho da tabela
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        let xPos = 10;
        headers.forEach((header, index) => {
          doc.rect(xPos, yPos, colWidths[index], 10);
          const lines = header.split('\n');
          if (lines.length > 1) {
            // Centralizar verticalmente textos em múltiplas linhas
            doc.text(lines[0], xPos + 1, yPos + 4);
            doc.text(lines[1], xPos + 1, yPos + 8);
          } else {
            doc.text(header, xPos + 1, yPos + 6);
          }
          xPos += colWidths[index];
        });

        yPos += 10;
        
        // Desenhar produtos da página
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        for (let index = 0; index < produtosPagina.length; index++) {
          const produto = produtosPagina[index];
          const numeroProduto = inicio + index + 1;
          xPos = 10;

          // Número
          doc.rect(xPos, yPos, colWidths[0], 20);
          doc.text(numeroProduto.toString(), xPos + 1, yPos + 12);
          xPos += colWidths[0];
          
          // Imagem
          doc.rect(xPos, yPos, colWidths[1], 20);
          if (incluirImagens && produto.urlImagens) {
            try {
              // Carregar imagem de forma mais eficiente
              const img = new Image();
              img.crossOrigin = "anonymous";
              
              const imagePromise = new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error('Timeout'));
                }, 2000); // Reduzido para 2 segundos
                
                img.onload = () => {
                  clearTimeout(timeout);
                  resolve(img);
                };
                img.onerror = () => {
                  clearTimeout(timeout);
                  reject(new Error('Failed to load image'));
                };
                img.src = produto.urlImagens;
              });
              
              const imgLoaded = await imagePromise;
              
              // Redimensionar imagem para caber na célula
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              // Dimensões da célula (convertendo mm para px - 1mm ≈ 3.78px)
              const cellWidth = (colWidths[1] - 2) * 3.78; // Largura da célula em px
              const cellHeight = 18 * 3.78; // Altura da célula em px (18mm)
              const maxHeightPx = 60; // Altura máxima em pixels (60px)
              
              // Calcular proporção mantendo a imagem dentro dos limites
              const aspectRatio = imgLoaded.width / imgLoaded.height;
              let imgWidth = cellWidth;
              let imgHeight = cellWidth / aspectRatio;
              
              // Se a altura for maior que o máximo, ajustar pela altura
              if (imgHeight > maxHeightPx) {
                imgHeight = maxHeightPx;
                imgWidth = maxHeightPx * aspectRatio;
              }
              
              // Se a largura for maior que a célula, ajustar pela largura
              if (imgWidth > cellWidth) {
                imgWidth = cellWidth;
                imgHeight = cellWidth / aspectRatio;
              }
              
              // Configurar canvas com tamanho calculado
              canvas.width = imgWidth;
              canvas.height = imgHeight;
              
              // Preencher fundo branco antes de desenhar a imagem
              ctx.fillStyle = '#FFFFFF';
              ctx.fillRect(0, 0, imgWidth, imgHeight);
              
              // Desenhar imagem redimensionada no canvas
              ctx.drawImage(imgLoaded, 0, 0, imgWidth, imgHeight);
              
              // Converter para data URL mantendo transparência (PNG)
              const imgData = canvas.toDataURL('image/png');
              
              // Calcular posição para centralizar a imagem na célula
              const xOffset = xPos + 1 + (cellWidth - imgWidth) / 2 / 3.78; // Converter de volta para mm
              const yOffset = yPos + 1 + (cellHeight - imgHeight) / 2 / 3.78; // Converter de volta para mm
              
              // Adicionar imagem centralizada ao PDF
              doc.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth / 3.78, imgHeight / 3.78);
            } catch (error) {
              console.log(`Erro ao carregar imagem do produto ${numeroProduto}:`, error.message);
              // Se falhar ao carregar imagem, mostrar texto
              doc.text("Sem imagem", xPos + 2, yPos + 12);
            }
          } else {
            doc.text("Sem imagem", xPos + 2, yPos + 12);
          }
          xPos += colWidths[1];
          
          // Produto
          doc.rect(xPos, yPos, colWidths[2], 20);
          const descricaoProduto = produto.descricao || "";
          const maxWidth = colWidths[2] - 2; // Largura da célula menos margem
          const lines = doc.splitTextToSize(descricaoProduto, maxWidth);
          doc.text(lines, xPos + 1, yPos + 8);
          xPos += colWidths[2];

          // GTN/EAN
          doc.rect(xPos, yPos, colWidths[3], 20);
          const gtin = produto.gtinEan || "";
          const gtinLines = doc.splitTextToSize(gtin, colWidths[3] - 2);
          doc.text(gtinLines, xPos + 1, yPos + 12);
          xPos += colWidths[3];

          // SKU
          doc.rect(xPos, yPos, colWidths[4], 20);
          const sku = produto.sku || "";
          const skuLines = doc.splitTextToSize(sku, colWidths[4] - 2);
          doc.text(skuLines, xPos + 1, yPos + 12);
          xPos += colWidths[4];

          // Lote
          doc.rect(xPos, yPos, colWidths[5], 20);
          doc.text(produto.lote || "", xPos + 1, yPos + 12);
          xPos += colWidths[5];

          // Data Validade
          doc.rect(xPos, yPos, colWidths[6], 20);
          doc.text(produto.dataValidade ? dayjs(produto.dataValidade).format("DD/MM/YY") : "", xPos + 1, yPos + 12);
          xPos += colWidths[6];

          // Estoque
          doc.rect(xPos, yPos, colWidths[7], 20);
          doc.text(produto.estoqueDisponivelCaixas?.toString() || "0", xPos + 1, yPos + 12);
          xPos += colWidths[7];

          // Unidade por Caixa
          doc.rect(xPos, yPos, colWidths[8], 20);
          doc.text(produto.quantidadePorCaixa?.toString() || "0", xPos + 1, yPos + 12);
          xPos += colWidths[8];

          // Preço por Caixa
          doc.rect(xPos, yPos, colWidths[9], 20);
          const precoCx = produto.precoCaixa ? produto.precoCaixa.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "R$ 0,00";
          doc.text(precoCx, xPos + 1, yPos + 12);
          xPos += colWidths[9];

          // Preço Unitário
          doc.rect(xPos, yPos, colWidths[10], 20);
          const precoUn = produto.precoUnitario ? produto.precoUnitario.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) : "R$ 0,00";
          doc.text(precoUn, xPos + 1, yPos + 12);
          xPos += colWidths[10];

          // Preço com ST (Preço Unitário com ST)
          doc.rect(xPos, yPos, colWidths[11], 20);
          const precoST = typeof produto.precoUnitarioComST === 'string'
            ? produto.precoUnitarioComST
            : produto.precoUnitarioComST
              ? produto.precoUnitarioComST.toLocaleString("pt-br", { style: "currency", currency: "BRL" })
              : "R$ 0,00";
          doc.text(precoST, xPos + 1, yPos + 12);
          xPos += colWidths[11];

          // Preço com FEE (Preço Unitário com Fee)
          doc.rect(xPos, yPos, colWidths[12], 20);
          const precoFee = typeof produto.precoUnitarioComFee === 'string'
            ? produto.precoUnitarioComFee
            : produto.precoUnitarioComFee
              ? produto.precoUnitarioComFee.toLocaleString("pt-br", { style: "currency", currency: "BRL" })
              : "R$ 0,00";
          doc.text(precoFee, xPos + 1, yPos + 12);

          yPos += 20;
        }
        
        // Custo total na última página
        if (pagina === paginasLimitadas - 1) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text(`Custo Total: ${location.state.custoTotal?.toLocaleString("pt-br", { style: "currency", currency: "BRL" }) || "R$ 0,00"}`, 10, yPos + 10);
        }
      }
      
      doc.save("produto.pdf");
    };

    // Aguardar um pouco mais para garantir que o componente esteja totalmente renderizado
    setTimeout(() => {
    generatePDF();
    }, 500);
  }, [data]);

  return (
    <div ref={pdfRef}>
      <div
        className="row"
        style={{ borderBottom: "1.5px solid rgba(0, 0, 0, 0.3)" }}
      >
        <div className="col-sm-2 col-md-2 col-lg-2">
          Número da Oportunidade <br />
          <strong>{location.state.codigo?.toString()?.padStart(6, "0")}</strong>
        </div>
        <div className="col-sm-2 col-md-2 col-lg-2">
          Data de Criação <br />
          <strong>
            {dayjs(location.state.dataAbertura).format("DD/MM/YYYY")}
          </strong>
        </div>
        <div className="col-sm-2 col-md-2 col-lg-2">
          Seller <br />
          <strong>{location.state.seller.label}</strong>
        </div>
        <div className="col-sm-2 col-md-2 col-lg-2">
          TP <br />
          <strong>TP {location.state.tp}</strong>
        </div>
      </div>

      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table>
          <TableHead>
            <TableRow>
              {[
                "",
                "Produto",
                "GTN/EAN",
                "SKU",
                "Lote",
                "Data de Validade",
                "Estoque Disponível (cx)",
                "Unidade por Caixa",
                "Preço por Caixa",
                "Preço Unitário",
                "Preço c/ ST",
                "Preço c/ FEE",
              ].map((header, index) => (
                <TableCell
                  key={header}
                  style={{
                    borderRight:
                      index < 11 ? "1.5px solid rgba(0, 0, 0, 0.3)" : "none",
                    fontWeight: "700",
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={index}
                style={{ height: "140px", minHeight: "140px" }}
              >
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  <img
                    src={row.urlImagens}
                    alt="Produto"
                    width="100"
                    style={{ maxHeight: "100px" }}
                  />
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.descricao}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.gtinEan}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.sku}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.lote}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.dataValidade
                    ? dayjs(row.dataValidade).format("DD/MM/YYYY")
                    : ""}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.estoqueDisponivelCaixas}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.quantidadePorCaixa}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                 
                  {row.precoCaixa?.toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.precoUnitario?.toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </TableCell>
                <TableCell
                  style={{ borderRight: "1.5px solid rgba(0, 0, 0, 0.3)" }}
                >
                  {row.precoUnitarioComST?.toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </TableCell>
                <TableCell>
                  {row.precoUnitarioComFee?.toLocaleString("pt-br", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "20px",
        }}
      >
        <div
          className="col-sm-2 col-md-2 col-lg-2"
          style={{
            display: "flex",
            borderTop: "1.5px solid rgba(0, 0, 0, 0.3)",
            alignItems: "center",
          }}
        >
          <div
            style={{
              paddingLeft: "8px",
              paddingRight: "8px",
              height: "100%",
              borderLeft: "1.5px solid rgba(0, 0, 0, 0.3)",
            }}
          >
            Custo Total:
          </div>
          <div
            style={{
              paddingLeft: "8px",
              height: "100%",
              borderLeft: "1.5px solid rgba(0, 0, 0, 0.3)",
            }}
          >
            <strong>
              {location.state.custoTotal?.toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
              })}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProdutoPDF;
